import { useCallback, useEffect, useState } from "react";

import { useRecipientsChanged } from "@platform/hooks/useRecipientsChanged";
import { saveDraft } from "@platform/utils/item.util";

import useEmailStore from "@store/useEmailStore";
import usePlatformStore from "@store/usePlatformStore";
import { useSyncEmail } from "../sync-email/hooks/useSyncEmail";

import { useGetThread } from "@screens/sync-email/hooks/useGetThread";
import { useGetTags } from "./hooks/useGetTags";
import { useGetThreadAttachments } from "@screens/sync-email/hooks/useGetThreadAttachments";
import { allAttachmentsSettled } from "@utils/attachment.util";
import { useSwitches } from "@hooks/useSwitches";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { RelationObjectType } from "@enums/relations.enum";
import { useOptimisticTags } from "@components/Sections/TagsSection/hooks/useOptimisticTags";
import { SubscribeSection } from "@components/Sections/SubscribeSection";
import { EmailTagsSection } from "@components/Sections/TagsSection";
import { AIContextSection } from "@components/Sections";
import { NewEntityButtons } from "@components/AddNewEntity";
import { ThreadHeader } from "@components/Headers";
import { View } from "@ui/View";

const REFETCH_INTERVAL = 3000;
const RECENT_THREAD_THRESHOLD_MS = 180_000; // 3 minutes

const CONTENT_TYPE = RelationObjectType.EMAIL_THREAD;

export default function ThreadScreen() {
  const { itemId, isComposeMode } = usePlatformStore();
  const { emailId, threadId } = useEmailStore();
  const { mutate: syncEmailMutate, isPending: isSyncEmailPending } = useSyncEmail();
  const { data: thread } = useGetThread();
  const {
    data: tags,
    refetch: refetchTags,
    isPending: areTagsPending,
  } = useGetTags(CONTENT_TYPE, threadId);
  const [showTags, setShowTags] = useState(false);
  const [showSubscribeSection, setShowSubscribeSection] = useState(false);
  const { schedule: scheduleShowTags } = useTimeoutRef();
  const { schedule: scheduleShowSubscribe } = useTimeoutRef();

  // Single tag-state owner, shared by the Tags section and the "New company/deal" buttons.
  const { currentTags, updateTags, onRemoveTag, setCurrentTags, temporaryTags } = useOptimisticTags(
    CONTENT_TYPE,
    threadId,
    tags || []
  );

  useEffect(() => {
    if (tags) setCurrentTags(tags);
  }, [tags, setCurrentTags]);

  const hasRecentEmail =
    !!thread?.latest_email?.created &&
    Date.now() - new Date(thread.latest_email.created).getTime() < RECENT_THREAD_THRESHOLD_MS;

  const {
    data: threadAttachments,
    refetch: refetchAttachments,
    isPending: areAttachmentsPending,
  } = useGetThreadAttachments(hasRecentEmail);

  const allAttachments = threadAttachments?.results?.flatMap((group) => group.attachments) ?? [];
  const areAttachmentsSettled = allAttachmentsSettled(allAttachments);

  const isLoadingAttachments = hasRecentEmail && (areAttachmentsPending || !areAttachmentsSettled);
  const shouldPollTags = isLoadingAttachments || (isComposeMode && !!emailId);

  const { isAvailable } = useSwitches();
  const enableEmailContextWindow = isAvailable("EMAIL_CONTEXT_WINDOW");

  const isThreadSubscribed = thread?.sync_thread || false;
  const showAIContext = !!thread?.id && !isComposeMode && enableEmailContextWindow;

  const isWaitingForInitialTags = areTagsPending && !!threadId;
  const isUpdatingTags = isWaitingForInitialTags || isLoadingAttachments;

  const handleSubscribe = () => {
    syncEmailMutate({
      identifier: itemId,
      sync_thread: true,
      is_draft: isComposeMode,
    });
  };

  const handleRecipientsChanged = useCallback(() => {
    // The handler is called only in the compose mode, and only if the email is synced
    if (!isComposeMode || !emailId) return;

    // 1. Save email draft
    saveDraft((success) => {
      if (success) {
        // 2. Re-sync with updated recipients — tag polling (3s interval) picks up new tags
        syncEmailMutate({
          identifier: itemId,
          sync_thread: !!thread?.sync_thread,
          is_draft: true,
        });
        // 3. Periodic polling in the next useEffect will periodically refetch tags
      }
    });
  }, [emailId, isComposeMode, itemId, syncEmailMutate, thread?.sync_thread]);

  useRecipientsChanged(handleRecipientsChanged);

  useEffect(() => {
    // Poll tags every 3s for two cases:
    // 1. Attachments still processing — document tags appear after attachment download completes
    // 2. Compose mode — recipient-based tags update after re-sync on recipients change
    if (!shouldPollTags) return;

    const intervalId = setInterval(() => {
      void refetchTags();
    }, REFETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [shouldPollTags, refetchTags]);

  useEffect(() => {
    // Poll attachments separately while they are still being processed
    if (!hasRecentEmail || areAttachmentsSettled) return;

    const intervalId = setInterval(() => {
      void refetchAttachments();
    }, REFETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hasRecentEmail, areAttachmentsSettled, refetchAttachments]);

  useEffect(() => {
    scheduleShowTags(() => setShowTags(true), 1000);
    scheduleShowSubscribe(() => setShowSubscribeSection(true), 3000);
  }, [scheduleShowTags, scheduleShowSubscribe]);

  return (
    <div className="flex flex-col justify-center items-center px-4 py-[8px_30px]">
      <ThreadHeader thread={thread} />

      <div className="relative z-0 flex flex-col gap-6 py-6 w-full animate-fade-in">
        <View.Condition if={showAIContext}>
          <AIContextSection thread={thread} />
        </View.Condition>

        <NewEntityButtons contentType={CONTENT_TYPE} onEntityCreated={(option) => updateTags([option])} />

        <View.Condition if={showTags}>
          <EmailTagsSection
            contentType={CONTENT_TYPE}
            className="animate-fade-in"
            isUpdating={isUpdatingTags}
            currentTags={currentTags}
            updateTags={updateTags}
            onRemoveTag={onRemoveTag}
            temporaryTags={temporaryTags}
          />
        </View.Condition>

        <View.Condition if={!isThreadSubscribed && showSubscribeSection}>
          <SubscribeSection
            isPending={isSyncEmailPending && !isComposeMode}
            onSubmit={handleSubscribe}
            syncedEmailsCount={thread?.synced_emails_count || 0}
          />
        </View.Condition>
      </div>
    </div>
  );
}
